import { Button, IllustratedMessage, Pagination, Progress } from '@eleveo/components-react';
import { PlusIcon, SearchNoneIcon } from '@eleveo/icons-react';
import { FormApi } from 'final-form';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { Center } from '../../components/LayoutNext/components/Center';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  ShiftTemplate,
  useBulkAssignSupervisedAgentsToShiftTemplateMutation,
  useCreateShiftTemplateMutation,
  useDeleteShiftTemplateMutation,
  useGetShiftTemplatesQuery,
  useUpdateShiftTemplateMutation,
} from '../../graphql/components';
import { ShiftTemplatesFormSidebar } from './ShiftTemplatesFormSidebar';
import { ShiftTemplatesTable } from './ShiftTemplatesTable';
import { ADD_SHIFT_TEMPLATE_BUTTON_TAG, CONTAINER_TAG, LIST_PAGINATION_TAG, TITLE_TAG } from './constants';
import { ShiftTemplatesFormValues } from './types';
import { cleanUninitializedShiftData } from './utils';

export const ShiftTemplatesScene: React.FC = () => {
  const { t } = useTranslation();
  const { userHasPermissions, userId } = useAuth();
  const { isSidebarOpen, isSidebarExpanded, openSidebar, expandSidebar, toggleSidebar, shrinkSidebar } = useSidebar();

  const [shiftTemplateToEdit, setShiftTemplateToEdit] = useState<ShiftTemplate | null>(null);
  const [shiftTemplateToDelete, setShiftTemplateToDelete] = useState<ShiftTemplate | null>(null);
  const [agentIdsToAssign, setAgentIdsToAssign] = useState<string[] | null>(null);

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 25,
  });

  const getShiftTemplatesVariables = {
    offset: pagination.pageSize * (pagination.page - 1),
    limit: pagination.pageSize,
  };

  const {
    data: shiftTemplatesData,
    loading: shiftTemplatesLoading,
    error: shiftTemplatesError,
    refetch: refetchShiftTemplatesData,
  } = useGetShiftTemplatesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    variables: getShiftTemplatesVariables,
  });
  const [createShiftTemplate, { client, loading: createShiftTemplateLoading }] = useCreateShiftTemplateMutation();
  const [updateShiftTemplate, { loading: updateShiftTemplateLoading }] = useUpdateShiftTemplateMutation();
  const [deleteShiftTemplate, { loading: deleteShiftTemplateLoading }] = useDeleteShiftTemplateMutation();
  const [bulkAssignSupervisedAgentsToShiftTemplate, { loading: agentsToAssignLoading }] =
    useBulkAssignSupervisedAgentsToShiftTemplateMutation();

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination((state) => ({
      ...state,
      page,
      pageSize: pageSize ?? state.pageSize,
    }));
  };

  const resetPagination = () => {
    handlePageChange(1);
  };

  const refetchShiftTemplatesDataFirstPage = async () => {
    resetPagination();
    await refetchShiftTemplatesData();
  };

  const resetStates = () => {
    setShiftTemplateToEdit(null);
    setAgentIdsToAssign(null);
  };

  const closeSidebar = () => {
    toggleSidebar();
    if (isSidebarExpanded) {
      shrinkSidebar();
    }
    resetStates();
  };

  const handleCreate = async (values: ShiftTemplatesFormValues) => {
    if (!values || !client) {
      return;
    }

    try {
      const createShiftTemplateResponse = await createShiftTemplate({
        variables: {
          shiftTemplate: {
            ...cleanUninitializedShiftData(values),
            effectiveDate: values.effectiveDate?.toString(),
          } as ShiftTemplate,
        },
      });

      if (agentIdsToAssign && createShiftTemplateResponse.data?.createShiftTemplate?.id) {
        await bulkAssignSupervisedAgentsToShiftTemplate({
          variables: {
            supervisorId: userId,
            data: {
              shiftTemplateId: createShiftTemplateResponse.data?.createShiftTemplate?.id,
              agentsIds: agentIdsToAssign,
            },
          },
        });
      }

      await refetchShiftTemplatesDataFirstPage();

      closeSidebar();
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const handleUpdate = async (values: ShiftTemplatesFormValues, pristine: boolean) => {
    if (!values || !client) {
      return;
    }

    try {
      if (!pristine) {
        await updateShiftTemplate({
          variables: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            id: shiftTemplateToEdit!.id,
            shiftTemplate: {
              ...cleanUninitializedShiftData(values),
              effectiveDate: values.effectiveDate?.toString(),
            } as ShiftTemplate,
          },
        });
      }

      if (agentIdsToAssign) {
        await bulkAssignSupervisedAgentsToShiftTemplate({
          variables: {
            supervisorId: userId,
            data: {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              shiftTemplateId: shiftTemplateToEdit!.id,
              agentsIds: agentIdsToAssign,
            },
          },
        });
      }

      await refetchShiftTemplatesDataFirstPage();

      closeSidebar();
    } catch (e) {
      // `customFetch()` will display error message
    }
  };
  const handleSubmit = (values: ShiftTemplatesFormValues, form: FormApi<ShiftTemplatesFormValues>) => {
    if (shiftTemplateToEdit === null) {
      handleCreate(values);
    } else {
      handleUpdate(values, form.getState().pristine);
    }
  };

  const mutationInProgress =
    deleteShiftTemplateLoading || createShiftTemplateLoading || agentsToAssignLoading || updateShiftTemplateLoading;

  const handlePrepareEdit = (shiftTemplate: ShiftTemplate) => {
    openSidebar();
    setShiftTemplateToEdit(shiftTemplate);
  };

  const handlePrepareCreate = () => {
    if (isSidebarExpanded) {
      shrinkSidebar();
    }

    if (!shiftTemplateToEdit) {
      toggleSidebar();
    }

    resetStates();
  };

  const handlePrepareAssignAgents = (shiftTemplate: ShiftTemplate) => {
    openSidebar();
    expandSidebar();
    setShiftTemplateToEdit(shiftTemplate);
  };

  const handleShowDeleteModal = (shiftTemplate: ShiftTemplate) => setShiftTemplateToDelete(shiftTemplate);
  const handleCloseDeleteModal = () => setShiftTemplateToDelete(null);

  const handleDelete = async () => {
    if (!shiftTemplateToDelete) {
      return;
    }

    const { id } = shiftTemplateToDelete;

    try {
      await deleteShiftTemplate({
        variables: {
          id,
        },
      });

      await refetchShiftTemplatesDataFirstPage();
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setShiftTemplateToDelete(null);
    }
  };

  const shiftTemplates = shiftTemplatesData?.shiftTemplates?.shiftTemplates ?? [];
  const skipRendering = (shiftTemplatesLoading && !shiftTemplates.length) || shiftTemplatesError;

  return (
    <>
      <Header title={t('shiftTemplate.shiftTemplate', { count: 2 })} testId={TITLE_TAG} />

      {userHasPermissions([Permissions.CreateShiftTemplate]) && (
        <Panel>
          <Button
            isSelected={isSidebarOpen && shiftTemplateToEdit === null}
            testId={ADD_SHIFT_TEMPLATE_BUTTON_TAG}
            onClick={handlePrepareCreate}
          >
            <PlusIcon />
            {t('shiftTemplate.addNew')}
          </Button>
        </Panel>
      )}

      <Content testId={CONTAINER_TAG}>
        {(shiftTemplatesLoading || mutationInProgress) && <Progress withOverlay />}

        {!skipRendering && shiftTemplates.length > 0 && (
          <ShiftTemplatesTable
            activeId={shiftTemplateToEdit?.id}
            shiftTemplates={shiftTemplates}
            onAssign={handlePrepareAssignAgents}
            onEdit={handlePrepareEdit}
            onDelete={handleShowDeleteModal}
          >
            <Pagination
              testId={LIST_PAGINATION_TAG}
              onPageChange={handlePageChange}
              selectedPage={pagination.page}
              selectedPageSize={pagination.pageSize}
              totalItems={shiftTemplatesData?.shiftTemplates?.totalCount || 0}
            />
          </ShiftTemplatesTable>
        )}

        {!skipRendering && shiftTemplates.length === 0 && (
          <Center>
            <IllustratedMessage heading={t('shiftTemplate.noEntries')} icon={<SearchNoneIcon />} />
          </Center>
        )}

        <ModalConfirmation
          isOpen={shiftTemplateToDelete !== null}
          title={t('shiftTemplate.deleteShiftTemplate')}
          acceptCaption={t('layout.action.delete')}
          cancelCaption={t('layout.action.cancel')}
          onAccept={handleDelete}
          onCancel={handleCloseDeleteModal}
        >
          <Trans
            i18nKey="shiftTemplate.deleteShiftTemplateBody"
            values={{ name: shiftTemplateToDelete?.name }}
            components={{ italic: <i /> }}
          />
        </ModalConfirmation>
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && (
          <ShiftTemplatesFormSidebar
            handleSubmit={handleSubmit}
            handleCancel={closeSidebar}
            shiftTemplate={shiftTemplateToEdit}
            agentIdsToAssign={agentIdsToAssign}
            setAgentIdsToAssign={setAgentIdsToAssign}
            shiftTemplates={shiftTemplates}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
